import { useState } from 'react';
import { Photo, PrintSize } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { ArrowLeft, Award } from 'lucide-react@0.487.0';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { CertificatePreview } from './CertificatePreview';
import { getOptimizedImageUrl } from '../utils/imageOptimization';

// Simple VisuallyHidden component for accessibility
function VisuallyHidden({ children }: { children: React.ReactNode }) {
  return (
    <span className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0">
      {children}
    </span>
  );
}

interface PhotoDialogProps {
  photo: Photo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddToCart: (photo: Photo, size: PrintSize) => void;
  onBuyNow: (photo: Photo, size: PrintSize) => void;
}

export function PhotoDialog({ photo, open, onOpenChange, onAddToCart, onBuyNow }: PhotoDialogProps) {
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [showCertificate, setShowCertificate] = useState(false);

  if (!photo) return null;

  const handleAddToCart = () => {
    const size = photo.sizes.find(s => s.id === selectedSize);
    if (size) {
      onAddToCart(photo, size);
      onOpenChange(false);
      setSelectedSize('');
    }
  };

  const handleBuyNow = () => {
    const size = photo.sizes.find(s => s.id === selectedSize);
    if (size) {
      onBuyNow(photo, size);
      onOpenChange(false);
      setSelectedSize('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      onOpenChange(open);
      if (!open) setSelectedSize('');
    }}>
      <DialogContent className="w-[98vw] max-w-[98vw] h-[96vh] max-h-[96vh] overflow-hidden p-0 bg-white border-none">
        {/* Hidden for screen readers - required for accessibility */}
        <VisuallyHidden>
          <DialogTitle>{photo.title}</DialogTitle>
          <DialogDescription>{photo.category}</DialogDescription>
        </VisuallyHidden>

        <button
          onClick={() => onOpenChange(false)}
          className="absolute left-2 top-2 lg:left-6 lg:top-6 z-50 px-4 py-3 lg:px-3 lg:py-2 hover:opacity-60 transition-opacity bg-white/90 rounded-full flex items-center gap-2"
        >
          <ArrowLeft className="h-5 w-5 lg:h-4 lg:w-4" strokeWidth={1.5} />
          <span className="text-[15px] lg:text-sm font-medium tracking-wide">Back</span>
        </button>

        <div className="relative flex flex-col lg:flex-row h-full overflow-y-auto lg:overflow-hidden">
          {/* AÐFERÐ 2: Ekkert padding - Myndin nær ALVEG út að brúnum */}
          <div className="relative flex items-center justify-center w-full lg:w-[70%] h-[70vh] lg:h-full flex-shrink-0 p-0">
            <img 
              src={getOptimizedImageUrl(photo.imageUrl, 'full')} 
              alt={`${photo.title} — ${photo.category} fine art photography print, full resolution view`}
              loading="eager"
              decoding="async"
              className="object-contain w-full h-full"
            />
          </div>

          <div className="relative w-full lg:w-[30%] p-6 lg:p-6 flex flex-col justify-between lg:overflow-y-auto flex-shrink-0">{/* Content column */}
            <div className="flex-1">
              <p className="text-neutral-700 mb-6 lg:mb-8 leading-relaxed text-[15px] lg:text-base">
                Printed on museum-quality fine art paper<br />
                Museum-quality archival standards<br />
                Digital certificate of authenticity sent to email
              </p>
              
              <button
                onClick={() => setShowCertificate(true)}
                className="text-[14px] lg:text-xs tracking-wide text-neutral-500 hover:text-neutral-900 transition-colors flex items-center gap-2 mb-8 lg:mb-10"
              >
                <Award className="w-5 h-5 lg:w-4 lg:h-4" strokeWidth={1.5} />
                View certificate of authenticity
              </button>
              
              {photo.sizes.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 p-4 mb-8 lg:mb-10">
                  <p className="text-[15px] lg:text-sm text-amber-800">
                    <strong>This image is not available for printing.</strong>
                    <br />
                    The image has not been uploaded in full resolution (300 DPI).
                  </p>
                </div>
              ) : (
                <div className="space-y-4 lg:space-y-5 mb-8 lg:mb-10">
                  <h4 className="text-neutral-900 text-[22px] lg:text-base font-medium">Choose size</h4>
                <RadioGroup value={selectedSize} onValueChange={setSelectedSize}>
                  <div className="space-y-2.5">
                    {photo.sizes.map((size) => (
                      <div
                        key={size.id}
                        className={`flex items-center gap-2.5 lg:gap-3 p-5 lg:p-4 border cursor-pointer transition-colors ${
                          selectedSize === size.id
                            ? 'border-black bg-neutral-50'
                            : 'border-neutral-200 hover:border-neutral-400'
                        }`}
                        onClick={() => setSelectedSize(size.id)}
                      >
                        <RadioGroupItem value={size.id} id={size.id} className="flex-shrink-0 w-6 h-6 lg:w-4 lg:h-4" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[20px] lg:text-sm tracking-wide mb-1 font-medium">{size.name}</div>
                          <p className="text-[17px] lg:text-xs text-neutral-500">
                            {size.dimensions}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <div className="text-[22px] lg:text-sm mb-1 font-bold">€{size.price}</div>
                          <p className="text-[16px] lg:text-xs text-neutral-500">
                            {size.available} available
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
                </div>
              )}

              <div className="border-t border-neutral-200 pt-6 space-y-3">
                <Button 
                  className="w-full bg-black text-white hover:bg-neutral-800 h-14 lg:h-12 text-[16px] lg:text-base font-medium"
                  onClick={handleBuyNow}
                  disabled={!selectedSize || photo.sizes.length === 0}
                >
                  Buy Now
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-neutral-300 hover:bg-neutral-50 h-14 lg:h-12 text-[16px] lg:text-base"
                  onClick={handleAddToCart}
                  disabled={!selectedSize || photo.sizes.length === 0}
                >
                  Add to Cart
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      <CertificatePreview
        open={showCertificate}
        onOpenChange={setShowCertificate}
        photo={photo}
        size={photo.sizes.find(s => s.id === selectedSize) || photo.sizes[0]}
        editionNumber={1}
      />
    </Dialog>
  );
}

export default PhotoDialog;